import { useEffect, useRef, useState } from "react";
import { CheckCircleOutlined, CloseCircleOutlined, EditOutlined, LoadingOutlined, WarningOutlined } from "@ant-design/icons";
import { updateDataSetRequest } from "store/dataSets/actions";
import { fetchDataSetById } from "./dataSetApi";
import { buildToolError } from "./errorEnvelope";
import { verifyUpdatedDataSet } from "./verify";
import type { ToolPartProps } from "./types";

interface UpdateDataSetInput {
    name?: string;
    description?: string;
}

interface UpdateDataSetOutput {
    success: boolean;
    cancelled?: boolean;
    error?: string;
    dataSetName?: string;
    dataSetDescription?: string;
    verified?: boolean;
    appliedChanges?: string[];
    unappliedChanges?: string[];
}

type Phase = "ready" | "denied" | "applying" | "applied" | "error";

export default function UpdateDataSetTool({ toolCallId, state, input, output, errorText, helpers }: ToolPartProps<UpdateDataSetInput, UpdateDataSetOutput>) {
    const { dispatch, addToolOutput, businessAreaId, schemaId, tenantId, dataSet, mode, permission, updateDataSetResult } = helpers;

    const [phase, setPhase] = useState<Phase>("ready");
    const [denyReason, setDenyReason] = useState<string | null>(null);
    const initialisedRef = useRef(false);
    const resolvedRef = useRef(false);
    // Snapshot of updateDataSetResult at dispatch; ignore stale prior results.
    const resultAtDispatchRef = useRef<unknown>(null);

    useEffect(() => {
        if (initialisedRef.current) return;
        if (state !== "input-available") return;
        initialisedRef.current = true;

        if (mode === "view" || !permission?.canEdit) {
            setPhase("denied");
            setDenyReason("You're in read-only mode for this data set, so I can't change its name or description.");
            return;
        }
        if (!dataSet || !dataSet.dataSetId) {
            setPhase("denied");
            setDenyReason("No data set is loaded.");
            return;
        }
    }, [state, mode, permission, dataSet]);

    useEffect(() => {
        if (resolvedRef.current) return;
        if (state === "output-available" || state === "output-error") return;
        if (phase === "denied") {
            resolvedRef.current = true;
            addToolOutput({ tool: "updateDataSet", toolCallId, output: { success: false, error: denyReason || "Cannot update data set." } });
        }
    }, [phase, denyReason, state, toolCallId, addToolOutput]);

    useEffect(() => {
        if (phase !== "applying") return;
        if (!updateDataSetResult) return;
        if (updateDataSetResult === resultAtDispatchRef.current) return;
        if (updateDataSetResult.success === true) {
            (async () => {
                let refreshed: { dataSetName?: string; dataSetDescription?: string } | null = null;
                let fetchErr: string | null = null;
                try {
                    if (tenantId && businessAreaId && schemaId && dataSet?.dataSetId) {
                        refreshed = await fetchDataSetById(tenantId, businessAreaId, schemaId, dataSet.dataSetId);
                    }
                } catch (e: any) {
                    fetchErr = e?.message || "fetch error";
                }
                const proposed: { name?: string; description?: string } = {};
                if (input?.name !== undefined && input.name !== dataSet?.dataSetName) proposed.name = input.name;
                if (typeof input?.description === "string" && input.description !== dataSet?.dataSetDescription) proposed.description = input.description;
                const report = verifyUpdatedDataSet(proposed, refreshed);
                setPhase("applied");
                if (!resolvedRef.current) {
                    resolvedRef.current = true;
                    addToolOutput({
                        tool: "updateDataSet",
                        toolCallId,
                        output: {
                            success: true,
                            dataSetName: refreshed?.dataSetName ?? input?.name ?? dataSet?.dataSetName,
                            dataSetDescription: refreshed?.dataSetDescription ?? input?.description ?? dataSet?.dataSetDescription,
                            verified: !fetchErr && report.verified,
                            appliedChanges: report.appliedChanges,
                            unappliedChanges: fetchErr ? [`could not re-fetch the data set to verify (${fetchErr})`] : report.unappliedChanges,
                        },
                    });
                }
            })();
        } else if (updateDataSetResult.success === false) {
            setPhase("error");
            if (!resolvedRef.current) {
                resolvedRef.current = true;
                addToolOutput({
                    tool: "updateDataSet",
                    toolCallId,
                    output: buildToolError(updateDataSetResult, "Update failed."),
                });
            }
        }
    }, [updateDataSetResult, phase, input, dataSet, toolCallId, addToolOutput]);

    const hasNameChange = !!input?.name && input.name !== dataSet?.dataSetName;
    const hasDescChange = typeof input?.description === "string" && input.description !== dataSet?.dataSetDescription;
    const nothingToDo = !hasNameChange && !hasDescChange;

    const handleApply = () => {
        if (!businessAreaId || !schemaId || !dataSet) return;
        const updatedDataSet: Record<string, unknown> = {};
        if (hasNameChange) updatedDataSet.dataSetName = input!.name;
        if (hasDescChange) updatedDataSet.dataSetDescription = input!.description;
        resultAtDispatchRef.current = updateDataSetResult;
        setPhase("applying");
        dispatch(updateDataSetRequest(businessAreaId, schemaId, dataSet, updatedDataSet, "assistant"));
    };

    const handleCancel = () => {
        if (!resolvedRef.current) {
            resolvedRef.current = true;
            addToolOutput({ tool: "updateDataSet", toolCallId, output: { success: false, cancelled: true } });
        }
    };

    return (
        <div className="rounded-2xl border border-[#001D3D]/40 bg-white p-3 text-sm text-zinc-800 shadow-sm">
            <div className="mb-2 flex items-center gap-2 text-[#001D3D]">
                <EditOutlined />
                <span className="text-xs font-semibold uppercase tracking-wider">Update data set</span>
            </div>
            <div className="text-base font-semibold text-zinc-900">{dataSet?.dataSetName || "(no data set)"}</div>
            {dataSet?.version !== undefined && <div className="text-[10px] text-zinc-500">v{dataSet.version}</div>}

            {phase === "denied" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {denyReason}
                </div>
            )}

            {phase !== "denied" && (
                <div className="mt-2 space-y-1 text-xs">
                    {hasNameChange && (
                        <div>
                            <span className="text-zinc-500">Rename to:</span> <span className="text-zinc-900">{input?.name}</span>
                        </div>
                    )}
                    {hasDescChange && (
                        <div>
                            <span className="text-zinc-500">New description:</span> <span className="text-zinc-900">{input?.description || "(empty)"}</span>
                        </div>
                    )}
                    {nothingToDo && <div className="text-zinc-500">No changes proposed.</div>}
                </div>
            )}

            {phase === "applied" && output?.verified !== false && (output?.unappliedChanges?.length || 0) === 0 && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-2 py-1 text-xs text-green-700">
                    <CheckCircleOutlined /> Updated.
                </div>
            )}
            {phase === "applied" && (output?.verified === false || (output?.unappliedChanges?.length || 0) > 0) && (
                <div className="mt-3 rounded-md border border-yellow-300 bg-yellow-50 px-2 py-1 text-xs text-yellow-800">
                    <div className="flex items-center gap-2 font-medium">
                        <WarningOutlined /> Some changes did not apply
                    </div>
                    <ul className="ml-4 mt-1 list-disc">
                        {(output?.unappliedChanges || []).map((c, i) => (
                            <li key={i}>{c}</li>
                        ))}
                    </ul>
                </div>
            )}
            {phase === "error" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> Update failed.
                </div>
            )}
            {state === "output-available" && output?.cancelled && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-2 py-1 text-xs text-zinc-600">
                    <CloseCircleOutlined /> Cancelled.
                </div>
            )}
            {state === "output-error" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {errorText || "Tool error."}
                </div>
            )}

            {phase === "ready" && state !== "output-available" && state !== "output-error" && (
                <div className="mt-3 flex items-center justify-end gap-2">
                    <button type="button" onClick={handleCancel} className="rounded-md border border-zinc-300 bg-white px-3 py-1 text-xs text-zinc-700 hover:bg-zinc-50">
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleApply}
                        disabled={nothingToDo}
                        className="rounded-md bg-[#001D3D] px-3 py-1 text-xs font-medium text-white hover:bg-[#002D5C] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Apply
                    </button>
                </div>
            )}
            {phase === "applying" && (
                <div className="mt-3 flex items-center justify-end gap-2 text-xs text-zinc-500">
                    <LoadingOutlined spin /> Applying...
                </div>
            )}
        </div>
    );
}
